import { getUserAvatarUrl, useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import { agentService } from '@common/api/agentService';
import { supabase } from '@common/api/authService';
import {
    Eye,
    EyeSlash,
    SignOut
} from '@phosphor-icons/react';
import React, {
    useEffect, useRef, useState
} from 'react';
import Modal from './Modal';

const ChangePasswordModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const { t } = useTranslation();
    const [showNew, setShowNew] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({password: newPassword});
            if (error) throw error;

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setNewPassword('');
                setSuccess(false);
            }, 1500);
        } catch (err: any) {
            setError(err.message || t('website.profileSettings.failedToUpdatePassword'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('website.profileSettings.updatePassword')} width="max-w-md">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#666F8D]">{t('website.profileSettings.newPassword')}</label>
                    <div className="relative">
                        <input 
                            type={showNew ? "text" : "password"}
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder={t('website.profileSettings.enterNewPassword')}
                            disabled={loading || success}
                            className="w-full rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-3 pr-10 text-sm font-normal text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20 disabled:bg-[#F8F9FC] disabled:text-[#8A93B2]"
                        />
                        <button type="button" aria-label={showNew ? t('website.profileSettings.hidePassword') : t('website.profileSettings.showPassword')} onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A93B2] transition-colors hover:text-[#1A162F]">
                            {showNew ?  <Eye size={18} weight="regular" /> : <EyeSlash size={18} weight="regular" />}
                        </button>
                    </div>
                    {error ? <div className="mt-2 text-sm text-red-600">{error}</div> : null}
                    {success ? <div className="mt-2 text-sm text-green-600">{t('website.profileSettings.passwordUpdatedSuccessfully')}</div> : null}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={loading || success}
                        className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:opacity-50"
                    >
                        {loading ? t('website.profileSettings.updating') : t('website.profileSettings.confirm')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const ProfileSettings = () => {
    const { t } = useTranslation();
    const {
        user, logout, profile, setProfile
    } = useAuth();

    // Use metadata full_name if available, fallback to profile userName or email
    const [name, setName] = useState(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '');
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 当 user 元数据更新后，同步更新 name 状态
    useEffect(() => {
        if (user?.user_metadata?.full_name) {
            setName(user.user_metadata.full_name);
        }
    }, [user?.user_metadata?.full_name]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            setAvatarError(t('website.profileSettings.selectImageFile'));
            return;
        }

        // 验证文件大小（最大 5MB）
        if (file.size > 5 * 1024 * 1024) {
            setAvatarError(t('website.profileSettings.imageSizeTooLarge'));
            return;
        }

        setIsUploadingAvatar(true);
        setAvatarError(null);

        try {
            // 1. 使用 agentService.uploadFile 上传文件
            const uploadResponse = await agentService.uploadFile(file);

            if (!uploadResponse.success) {
                throw new Error(uploadResponse.message || t('website.profileSettings.uploadFailed'));
            }

            // 2. 更新 Supabase 用户元数据
            const { data: updateData, error: updateError } = await supabase
                .auth
                .updateUser({data: {avatar_url: uploadResponse.file_url}});

            if (updateError) {
                throw updateError;
            }

        } catch (error: any) {
            console.error('Avatar upload failed:', error);
            setAvatarError(error.message || t('website.profileSettings.failedToUploadAvatar'));
        } finally {
            setIsUploadingAvatar(false);
            // 重置文件输入
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleNameSubmit = async () => {
        if (!name || name === user?.user_metadata?.full_name) return;

        setIsUpdatingName(true);
        try {
            // 更新 Supabase 用户元数据
            const { data: updateData, error: updateError } = await supabase.auth.updateUser({data: {full_name: name}});

            if (updateError) {
                throw updateError;
            }

            // 更新成功后，Supabase 会自动触发 onAuthStateChange 事件
            // AuthContext 中的 user 对象会自动更新，无需手动刷新
            console.log('姓名更新成功', updateData);

        } catch (error) {
            console.error('Failed to update name:', error);
        } finally {
            setIsUpdatingName(false);
        }
    };

    const handleLogout = () => {
        logout();
    };

    // 使用 user 的 updated_at 作为缓存破坏参数，确保头像更新后立即显示
    const cacheBuster = user?.updated_at ? String(new Date(user.updated_at).getTime()) : '';
    const avatarUrl = getUserAvatarUrl(user, { cacheBuster, fallbackName: name || 'User' });

    return (
        <div className="max-w-3xl">
            <div className="flex items-center gap-6 mb-8">
                {/* Avatar */}
                <div className="flex flex-col gap-2">
                    <div className="relative group cursor-pointer shrink-0" onClick={() => !isUploadingAvatar && fileInputRef.current?.click()}>
                        <img
                            src={avatarUrl}
                            className={`h-24 w-24 rounded-full border-2 border-white object-cover shadow-[0_10px_24px_rgba(26,22,47,0.10)] ${isUploadingAvatar ? 'opacity-50' : ''}`}
                            alt={t('website.profileSettings.profileAlt')}
                        />
                        <div className={`absolute inset-0 bg-black/40 rounded-full flex items-center justify-center transition-opacity ${isUploadingAvatar ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                            {isUploadingAvatar ? (
                                <div className="text-xs font-medium text-white">{t('website.profileSettings.uploading')}</div>
                            ) : (
                                <span className="text-xs font-medium text-white">{t('website.profileSettings.change')}</span>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                                e.preventDefault();
                                handleAvatarUpload(e);
                            }}
                            disabled={isUploadingAvatar}
                        />
                    </div>
                    {avatarError ? <div className="max-w-[100px] text-center text-xs text-red-600">{avatarError}</div> : null}
                </div>

                {/* Name and Logout */}
                <div className="flex-1 flex items-end justify-between">
                    <div className="flex-1 max-w-md">
                        <label className="mb-2 block text-sm font-medium text-[#666F8D]">{t('website.profileSettings.name')}</label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={handleNameChange}
                            onBlur={handleNameSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleNameSubmit();
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                            disabled={isUpdatingName}
                            className="w-full rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-3 text-sm font-normal text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20 disabled:bg-[#F8F9FC] disabled:text-[#8A93B2]"
                        />
                    </div>

                    <div className="mb-[2px] ml-4 flex items-center gap-3">
                        {/* Logout Button */}
                        <button 
                            className="rounded-[10px] border border-[#E8EBF3] bg-white p-3 text-[#666F8D] shadow-[0_8px_20px_rgba(26,22,47,0.06)] transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                            onClick={handleLogout}
                            title={t('website.clientAuth.logOut')}
                        >
                            <SignOut size={20} weight="regular" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-8 rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-6 shadow-[0_12px_30px_rgba(26,22,47,0.06)]">
                {/* Email */}
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#666F8D]">{t('website.profileSettings.email')}</label>
                    <div className="text-sm font-normal text-[#1A162F]">{user?.email}</div>
                </div>

                {/* Password */}
                <div className="border-t border-[#E8EBF3] pt-8">
                    <label className="mb-4 block text-sm font-medium text-[#1A162F]">{t('website.profileSettings.password')}</label>
                    <div className="flex items-center justify-between">
                        <div className="text-2xl tracking-widest text-[#8A93B2]">••••••••••••</div>
                        <button 
                            onClick={() => setIsPasswordModalOpen(true)}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
                        >
                            {t('website.profileSettings.updatePassword')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Password Modal */}
            <ChangePasswordModal 
                isOpen={isPasswordModalOpen} 
                onClose={() => setIsPasswordModalOpen(false)}
            />
        </div>
    );
};

export default ProfileSettings;
